<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StrokeCoinImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { formatUnshortenedNumber } from '@/helpers/utils'
import BaseButton from '@/components/base/BaseButton.vue'
import { ref } from 'vue'
import api from '@/services/api'
import { useConfig } from '@/composables/game/config'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { usePlayer } from '@/composables/game/player'
import { useToaster } from '@/composables/game/toasters'

const props = defineProps<{
	show: boolean
	cardsToUpgradeCount: number
	totalProfitDelta: number
	totalCoins: number
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { updatePlayer } = usePlayer()
const { updateConfig } = useConfig()
const { startCoinAnimation } = useFlyingCoins()
const { notify } = useToaster()

const isLoading = ref(false)

async function upgradeAllCardsWithGoldenCoins() {
  if (isLoading.value) return

  isLoading.value = true

  try {
    const response = await api.upgradeAllCardsWithGoldenCoins()
    const updatedCards = props.cardsToUpgradeCount
    updateConfig(response.config)
    updatePlayer(response.playerStats)
    startCoinAnimation()
    notify({ title: `Batch upgraded ${updatedCards} cards`, type: 'success' })
    emit('hide')
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center relative mt-2">
      <img
        class="size-[135px] object-cover"
        src="@/assets/images/plant.webp"
      >
      <div class="mt-2.5 text-center uppercase text-[24px] leading-8 -tracking-[0.48px] font-bold">
        BATCH UPGRADE
      </div>
      <div class="max-w-[296px] text-center text-white/[.6] text-base font-medium">
        Use your available coins to upgrade cards in one go
      </div>
      <div
        class="mt-6 rounded-lg p-0.5 bg-[linear-gradient(180deg,_#FFF246_0%,_#E5B80B_37%,_#FFF571_69%,_#E5B80B_100%)]"
      >
        <div class="px-8 py-3 rounded-lg bg-dark-bottom-layer w-full h-full text-lg font-semibold">
          {{ cardsToUpgradeCount }} {{ `card${cardsToUpgradeCount > 1 ? 's' : ''}` }}
        </div>
      </div>
      <div class="mt-1.5 flex items-center gap-x-2 text-sm">
        <div class="text-white/60 font-medium">
          Profit per hour
        </div>
        <div class="flex items-center gap-x-1">
          <img
            :src="StrokeCoinImage"
            class="h-4 w-auto object-cover"
          >
          <div class="text-white font-semibold -tracking-[0.28px]">
            +{{ formatUnshortenedNumber(totalProfitDelta) }}
          </div>
        </div>
      </div>
      <BaseButton
        class="mt-12 w-full"
        style-type="secondary"
        :loading="isLoading"
        @click="upgradeAllCardsWithGoldenCoins"
      >
        <template #leftIcon>
          <img
            :src="StrokeCoinImage"
            class="h-full w-auto object-cover"
          >
        </template>
        {{ formatUnshortenedNumber(totalCoins) }}
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>